import { useQuery } from '@tanstack/react-query';
import { fetchToken } from '@agissc/ktag-map';
import { StoreMutatorIdentifier } from 'zustand';
import config from '../../../utils/AppConfig';

export interface LandownerResponse {
  queriesLeft: number;
  results: Result[];
}

export interface Result {
  eigentum: Eigentum;
  eigentuemer: Eigentuemer[];
}

export interface Eigentum {
  bezeichnung: string; // "STW Küttigen/8591-1"
  anteilZaehler: string; // "121";
  anteilNenner: string; // "1000";
  art: string | null; // null;
  zusatz: StoreMutatorIdentifier | null; // null,
  gemeinde: string; // "Küttigen",
  grundstuecknummer: number; // 8591
}

export interface Eigentuemer {
  [key: string]: any;
}

export const useRemainingRequests = () => {
  const fetchRemainingRequests = async () => {
    const url = config.apis.landownersRemainingRequests;

    const headers = new Headers();
    try {
      const token = await fetchToken();
      headers.set('Authorization', `Bearer ${token}`);

      const requestApi = new Request(url, { headers });
      const response = await fetch(requestApi);
      const remainingRequests: number = await response.json();
      return remainingRequests;
    } catch {
      throw new Error('fetchRemainingRequestsError');
    }
  };

  return useQuery<number>({
    queryKey: ['remainingRequests'],
    queryFn: () => fetchRemainingRequests(),
    staleTime: 1000,
    retry: 2,
  });
};

const mockApiUrl = (egrid: string) => {
  switch (egrid) {
    case 'CH752254976159':
      return 'landownerMockup_Alleineigentum_naturerlich.json';
    case 'CH635497613115':
      return 'landownerMockup_Gesamteigentum_einfache Gesellschaft.json';
    case 'CH782254619782':
      return 'landownerMockup_Alleineigentum_juristisch.json';
    case 'CH795497226167':
      return 'landownerMockup_Mitteigentum.json';
    case 'CH508661385489':
      return 'landownerMockup_2Adressen.json';
    case 'CH509761375486':
      return 'landownerMockup_Miteigentum_Grundstück.json';
    case 'CH627687235273':
      return 'landownerMockup_Erlinsbach_4098.json';
    case 'CH409189706715':
      return 'landownerMockup_Grundstück_Art.json';
    case 'CH743256848858':
      return 'landownerMockup_Grundstück_Gemeinschaft.json';
    default:
      return 'landownerMockup_STW_Grundstück.json';
    // apiUrl = './landownerMockup_empty.json';
  }
};

const useLandowners = (egrid: string) => {
  const fetchLandowners = async (url: string) => {
    const headers = new Headers();
    if (process.env.NODE_ENV !== 'development') {
      const token = await fetchToken();
      headers.set('Authorization', `Bearer ${token}`);
    }

    const requestApi = new Request(url, { headers });
    const response = await fetch(requestApi);

    if (response.ok !== true) {
      let errorMessage;
      switch (response.status) {
        case 403:
          {
            const json = await response.json();
            if (json.code === 1) {
              errorMessage = 'landowner_error403_message';
            } else {
              errorMessage = 'landowner_errorDefault_message';
            }
          }
          break;
        case 429:
          errorMessage = 'landowner_error429_message';
          break;
        default:
          errorMessage = 'landowner_errorDefault_message';
      }
      throw new Error(errorMessage);
    }
    const json = await response.json();
    const landownerResponse: LandownerResponse = json;
    return landownerResponse;
  };

  const apiUrl =
    process.env.NODE_ENV === 'development'
      ? new URL(`/mock-data/${mockApiUrl(egrid)}`, import.meta.url).href
      : `${config.apis.landowners}/${egrid}`;

  return useQuery<LandownerResponse>({
    queryKey: ['landowners', egrid],
    queryFn: () => fetchLandowners(apiUrl),
    gcTime: 3600000,
    staleTime: Infinity,
    retry: false,
  });
};

export default useLandowners;
